// Planes de entrenamiento para alto rendimiento 8-9 años.
// Base científica: LTAD "Learn to Train" (Balyi & Way), FIFA 11+ Kids,
// Côté et al. (deliberate play), Ford et al. (J Sports Sci), Lloyd & Oliver (YPD).

export const AGE_GROUP = '8-9 años — Alto Rendimiento';
export const SESSION_DURATION = '2 horas';

export const TRAINING_PLANS = [
  // ══════════════════════════════════════════════════════════════════
  // PLAN A — TÉCNICA & COORDINACIÓN
  // ══════════════════════════════════════════════════════════════════
  {
    id: 'tecnica',
    code: 'A',
    title: 'Técnica & Coordinación',
    color: 'emerald',
    objective: 'Habilidad individual con balón + ABC coordinativo',
    science: 'Ventana sensible 7-10 años, máxima plasticidad neuromotora (Hirtz & Starosta, 2002). Élite profesional acumuló >10.000 toques/semana antes de los 12 años (Ford et al., 2010).',
    targetRPE: '5/10',
    blocks: [
      {
        time: '0:00-0:20',
        name: 'Activación',
        content: 'FIFA 11+ Kids + juego de espejo con balón. Movilidad dinámica + ABC coordinativo.',
        image: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800&q=80',
        objective: 'Elevar temperatura corporal, activar sistema neuromuscular y reducir riesgo de lesión.',
        equipment: ['8-10 conos', 'Balón por jugador', '1 escalera de coordinación (opcional)'],
        exercises: [
          {
            name: 'Trote dinámico + movilidad articular',
            duration: '5 min',
            description: 'Jugadores trotan en un cuadrante 15x15m. Cada 30s el coach grita un movimiento: círculos de brazos, rodillas al pecho, talones al glúteo, zancadas laterales.',
            keyPoints: [
              'Rango completo de movimiento',
              'Intensidad creciente (trote → trote medio)',
              'Respiración nasal',
            ],
          },
          {
            name: 'FIFA 11+ Kids parte 1 (cuadrantes)',
            duration: '6 min',
            description: '3 estaciones de 2 min: equilibrio monopodal (30s cada pierna) / sentadillas con balón en alto / saltos laterales sobre línea.',
            keyPoints: [
              'Rodilla alineada con dedo gordo',
              'Core activo, pecho arriba',
              'Aterrizaje suave',
            ],
          },
          {
            name: 'Juego del espejo con balón',
            duration: '5 min',
            description: 'Parejas frente a frente a 3m. Uno mueve el balón (toques, planta, paredes) y el otro imita como espejo. Cambio cada 45s.',
            keyPoints: [
              'Mirar los ojos del compañero, no el balón',
              'Reacción rápida',
              'Ambos pies',
            ],
          },
          {
            name: 'ABC coordinativo con escalera',
            duration: '4 min',
            description: 'Carioca, skipping alto, frecuencia dentro de la escalera, saltos a un pie. Si no hay escalera, usar conos a 50cm.',
            keyPoints: [
              'Cabeza levantada',
              'Brazos activos',
              'Contacto corto con el piso',
            ],
          },
        ],
        coachCues: [
          '"¡Rodillas arriba!" en skipping',
          '"Mirá al compañero, no la pelota"',
          '"Suave al aterrizar, como un gato"',
        ],
        commonMistakes: [
          'Intensidad demasiado alta demasiado temprano',
          'Saltar la movilidad articular (esencial para prevenir lesiones)',
          'No corregir alineación de rodilla en sentadillas',
        ],
        variations: {
          easier: 'Reducir la escalera a 3 cuadros y permitir caminar entre cuadros.',
          harder: 'Agregar balón en cada ejercicio (ej: sentadilla sosteniendo balón arriba con los pies).',
        },
      },
      {
        time: '0:20-0:55',
        name: 'Técnica analítica — circuito',
        content: 'Circuito 6 estaciones (90s trabajo / 30s descanso): conducción, dominio, golpeo ambos pies, 1v1, control orientado, finalización.',
        image: 'https://images.unsplash.com/photo-1551958219-acbc608c6377?w=800&q=80',
        objective: 'Acumular volumen de contactos con balón en gestos técnicos fundamentales, con ambos pies.',
        equipment: ['Balón por jugador', '12 conos', '2 arcos chicos', 'Platillos'],
        exercises: [
          {
            name: 'Estación 1 — Conducción en zigzag',
            duration: '90s trabajo / 30s descanso',
            description: '5 conos en zigzag separados 1.5m. Ida con pie dominante (interior), vuelta con no dominante (exterior). Máxima velocidad sin perder control.',
            keyPoints: [
              'Toques cortos (1 toque por cono)',
              'Cuerpo bajo, centro de gravedad bajo',
              'Usar ambas caras del pie',
            ],
          },
          {
            name: 'Estación 2 — Dominio variado',
            duration: '90s trabajo / 30s descanso',
            description: 'En un círculo de 3m de diámetro: dominio al aire, alterno pies, luego muslo-pie, luego pecho-pie.',
            keyPoints: [
              'Tobillo firme',
              'Golpe debajo del balón',
              'Mantener balón a altura de cintura',
            ],
          },
          {
            name: 'Estación 3 — Golpeo al arco',
            duration: '90s trabajo / 30s descanso',
            description: 'Balón quieto a 8m del arco chico. Tira 2 con dominante, 2 con no dominante. Busca entrar al arco con precisión, no fuerza.',
            keyPoints: [
              'Pie de apoyo al lado del balón, apuntando al arco',
              'Golpear con empeine (no punta)',
              'Seguir la jugada después del tiro',
            ],
          },
          {
            name: 'Estación 4 — 1v1 con defensor pasivo',
            duration: '90s trabajo / 30s descanso',
            description: 'Atacante parte con balón, defensor activa presión al segundo toque. Objetivo: desbordar por cualquier lado y llegar a línea de fondo.',
            keyPoints: [
              'Cambio de ritmo',
              'Amago con el cuerpo antes que con el pie',
              'Balón lejos del defensor',
            ],
          },
          {
            name: 'Estación 5 — Control orientado',
            duration: '90s trabajo / 30s descanso',
            description: 'Compañero pasa desde 8m. El jugador controla con el pie contrario al que recibe, orientándose hacia la próxima zona (marcada con cono).',
            keyPoints: [
              'Primer toque hacia donde vas a jugar',
              'Perfil abierto al recibir',
              'Mirada antes de recibir',
            ],
          },
          {
            name: 'Estación 6 — Finalización',
            duration: '90s trabajo / 30s descanso',
            description: 'Conducción 10m + remate al arco. Alternar pierna. Después del remate, rebote y segundo tiro rápido.',
            keyPoints: [
              'Último toque te deja el balón para pegarle',
              'Cabeza arriba antes del remate',
              'Reacción al rebote',
            ],
          },
        ],
        coachCues: [
          '"¡Cabeza arriba!" (scan)',
          '"¿Dónde está el espacio?"',
          '"Pie débil" (recordar alternar)',
        ],
        commonMistakes: [
          'Usar solo pie dominante (insistir 50/50)',
          'Mirar el balón todo el tiempo',
          'Pasar de estación sin descansar 30s',
        ],
        variations: {
          easier: 'Reducir distancia al arco a 5m, conos más separados.',
          harder: 'Agregar límite de toques (máx 2 por estación), defensor activo en estación 4.',
        },
      },
      {
        time: '0:55-1:20',
        name: 'Técnica aplicada — rondos',
        content: 'Rondos con progresión de densidad: 4v1 → 5v2 → 6v3. Toque obligatorio con pie no dominante cada 3 pases.',
        image: 'https://images.unsplash.com/photo-1624880357913-a8539238245b?w=800&q=80',
        objective: 'Transferir técnica individual a situación real de juego con presión y toma de decisión.',
        equipment: ['2 balones', '8 conos para delimitar círculos', 'Petos 2 colores'],
        exercises: [
          {
            name: 'Rondo 4v1',
            duration: '8 min (2 series de 4 min)',
            description: 'Círculo de 6m diámetro. 4 jugadores afuera pasan, 1 al medio intercepta. Si el defensor toca el balón, entra el que la perdió.',
            keyPoints: [
              'Moverse antes de recibir (3er paso)',
              'Perfil abierto al campo',
              'Pase entre líneas si es posible',
            ],
          },
          {
            name: 'Rondo 5v2 con regla de pie débil',
            duration: '10 min',
            description: 'Círculo 8m, 5 afuera, 2 al medio. Cada 3 pases, el siguiente debe ser con pie no dominante. Si no, pierden posesión.',
            keyPoints: [
              'Comunicación verbal obligatoria',
              'Apoyos dobles (el que pasa se vuelve a ofrecer)',
              'Cambio de ritmo en el pase',
            ],
          },
          {
            name: 'Rondo 6v3 con porterías chicas',
            duration: '7 min',
            description: 'Cuadrado 10x10m, 6 con posesión contra 3 recuperadores. Los 6 pueden marcar gol en 2 mini-porterías ubicadas en los bordes.',
            keyPoints: [
              'Buscar espacios amplios',
              'Filtrar pases entre defensores',
              '3ros hombres (el que recibe busca al libre)',
            ],
          },
        ],
        coachCues: [
          '"¡Antes de recibir, mirá!"',
          '"¿Primera intención?"',
          '"Apoyá al que la tiene"',
        ],
        commonMistakes: [
          'Quedarse estático después de pasar',
          'Pases solo al lateral (no filtrar)',
          'Perder paciencia y forzar el pase',
        ],
        variations: {
          easier: 'Aumentar el tamaño del círculo a 8m y sacar la regla del pie débil.',
          harder: 'Límite de 2 toques máximo, rondo 5v3 o reducir espacio a 5m.',
        },
      },
      {
        time: '1:20-1:45',
        name: 'Juego libre — SSG 3v3',
        content: 'SSG 3v3 en campo 20x15m. Regla: control orientado obligatorio antes de pase. 4 series de 4 min, 90s descanso.',
        image: 'https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=800&q=80',
        objective: 'Aplicar lo técnico en un contexto de juego real con alta densidad de acciones por jugador.',
        equipment: ['Balones', '2 arcos chicos', 'Petos 2 colores', 'Conos para campo'],
        exercises: [
          {
            name: 'SSG 3v3 con arcos chicos',
            duration: '16 min (4 series de 4 min + 90s descanso)',
            description: 'Campo 20x15m, 2 equipos de 3. Porterías de conos (1.5m ancho). Todos los goles valen. Regla técnica: al recibir, obligado a hacer control orientado (no puede devolver al primer toque).',
            keyPoints: [
              'Control orientado hacia el arco rival',
              'Triángulos siempre (apoyar al que tiene el balón)',
              'Transiciones rápidas al perder/recuperar',
              'Presión del equipo entero',
            ],
          },
        ],
        coachCues: [
          '"Primer toque al frente"',
          '"¿Quién apoya?"',
          '"¡Transición!" (al perder el balón)',
        ],
        commonMistakes: [
          'No respetar la regla del control orientado',
          'Amontonarse todos cerca del balón',
          'No correr en transición defensiva',
        ],
        variations: {
          easier: 'Campo más grande (25x20m), permitir 2 toques.',
          harder: 'Agregar comodín (3v3+1), reducir a 18x12m.',
        },
      },
      {
        time: '1:45-2:00',
        name: 'Cierre — skill challenge y feedback',
        content: 'Skill challenge individual (malabares, diana) + movilidad articular suave + círculo de feedback.',
        image: 'https://images.unsplash.com/photo-1606924735276-fbb5b325e933?w=800&q=80',
        objective: 'Bajar la intensidad, reforzar autoeficacia individual y cerrar con metacognición.',
        equipment: ['Balón por jugador', 'Diana o círculo de conos para tiros'],
        exercises: [
          {
            name: 'Skill challenge',
            duration: '8 min',
            description: 'Cada jugador elige UNO: (a) máximo de malabares en 1 min, (b) 10 tiros a diana pequeña desde 8m, (c) conducción slalom contrarreloj. Registrás el resultado en el perfil.',
            keyPoints: [
              'Foco en superar la marca personal',
              'Sin comparar con otros',
              'Celebrar el intento',
            ],
          },
          {
            name: 'Vuelta a la calma + movilidad',
            duration: '4 min',
            description: 'Caminata + movilidad articular dinámica suave (círculos de cadera, rotación de tronco, estiramiento de cuádriceps en movimiento).',
            keyPoints: [
              'NO estirar estático (no recomendado <10 años)',
              'Respiración profunda',
              'Hidratación',
            ],
          },
          {
            name: 'Círculo de feedback',
            duration: '3 min',
            description: 'Sentados en círculo. Cada uno dice: (1) una cosa que hizo bien, (2) una cosa a mejorar. Coach cierra con agradecimiento grupal.',
            keyPoints: [
              'Escuchar sin interrumpir',
              'Feedback específico (no "todo bien")',
              'Cerrar con buen clima',
            ],
          },
        ],
        coachCues: [
          '"¿Qué aprendiste hoy?"',
          '"¿Qué vas a practicar en casa?"',
          '"¡Buen trabajo, equipo!"',
        ],
        commonMistakes: [
          'Saltarse el círculo de feedback por tiempo',
          'Estiramiento estático (no va a esta edad)',
          'No registrar el skill challenge (pierde valor el histórico)',
        ],
        variations: {
          easier: 'Challenge: lo que cada uno se sienta cómodo eligiendo.',
          harder: 'Diana más lejos, objetivos más altos.',
        },
      },
    ],
    loadLimits: [
      '400+ contactos con balón por jugador',
      'RPE promedio ≤ 5/10',
      'Sin cargas externas / pesos',
    ],
  },

  // ══════════════════════════════════════════════════════════════════
  // PLAN B — TÁCTICA & TOMA DE DECISIONES
  // ══════════════════════════════════════════════════════════════════
  {
    id: 'tactica',
    code: 'B',
    title: 'Táctica & Toma de Decisiones',
    color: 'blue',
    objective: 'Cognición ejecutiva en contexto de juego',
    science: 'Constraints-Led Approach (Renshaw et al., 2010) + Teaching Games for Understanding (Bunker & Thorpe). Jugadores élite se diferencian por anticipación y scanning, no velocidad de reacción (Roca et al., 2012, J Exp Psychol).',
    targetRPE: '6/10 (alta carga mental)',
    blocks: [
      {
        time: '0:00-0:15',
        name: 'Activación con atención dividida',
        content: 'Persecuciones con reglas cambiantes (atención dividida). El coach cambia la regla cada 45s.',
        image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&q=80',
        objective: 'Activar el sistema nervioso + trabajar atención alternante y flexibilidad cognitiva.',
        equipment: ['Petos de 3-4 colores', 'Conos para delimitar zona 20x20m'],
        exercises: [
          {
            name: 'Persecución con pares/impares',
            duration: '5 min',
            description: 'Cada jugador tiene un número (1-10) pegado o dicho. Coach grita "pares a impares" → los pares persiguen a los impares. Cambia a "impares a pares" cada 30s.',
            keyPoints: [
              'Memorizar el número propio',
              'Reaccionar al cambio de regla',
              'Cabeza arriba',
            ],
          },
          {
            name: 'Persecución por color',
            duration: '5 min',
            description: 'Jugadores con peto de color. Coach grita un color → ese color debe huir, los demás persiguen. Coach cambia al grito "¡cambio!": el color perseguidor ahora huye.',
            keyPoints: [
              'Identificar tu color y el de los demás',
              'Anticipar el cambio',
              'Scan permanente',
            ],
          },
          {
            name: 'Stroop futbolero',
            duration: '5 min',
            description: 'Coach grita un color (ej: "rojo") pero señala otro cono (azul). Los jugadores deben ir al COLOR DICHO, no al señalado. Mide control inhibitorio.',
            keyPoints: [
              'Escuchar más que ver',
              'No reaccionar al primer estímulo',
              'Atención sostenida',
            ],
          },
        ],
        coachCues: [
          '"¡Atención a la regla!"',
          '"¿Escuchaste o viste?"',
          '"Cabeza arriba, mirando todo"',
        ],
        commonMistakes: [
          'Jugadores que se paralizan al cambio de regla',
          'Mirar solo al coach, no al entorno',
          'Colisiones por falta de scanning',
        ],
        variations: {
          easier: 'Menos reglas, cambios cada 60s.',
          harder: 'Agregar balón en los pies mientras se persiguen.',
        },
      },
      {
        time: '0:15-0:35',
        name: 'Percepción y scanning',
        content: 'Juegos donde el jugador debe gritar número de dedos del coach o color de peto antes de recibir el balón.',
        image: 'https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800&q=80',
        objective: 'Desarrollar la habilidad de escanear el entorno ANTES de recibir (predictor #1 de rendimiento élite).',
        equipment: ['Balones', 'Petos de distinto color', 'Conos'],
        exercises: [
          {
            name: 'Recibe-mira-gritá',
            duration: '8 min',
            description: 'Parejas a 10m. Mientras A pasa a B, el coach muestra dedos detrás de A (1, 2, 3, 4). B debe gritar el número ANTES de controlar. Si no grita o se equivoca, punto.',
            keyPoints: [
              'Mirada antes del pase',
              'Escanear aunque parezca "perder tiempo"',
              'Control con mirada ya hecha',
            ],
          },
          {
            name: '3v3 con scan obligatorio',
            duration: '12 min',
            description: 'Partido 3v3 normal, pero un asistente aparece en los bordes del campo con un peto de color random cada 5-10s. Antes de cada pase, el jugador debe decir el color del último peto visto.',
            keyPoints: [
              'Mirar más allá del balón',
              'Scan en cada pausa',
              'Comunicar al equipo',
            ],
          },
        ],
        coachCues: [
          '"¿Qué viste ahora?"',
          '"¡Mirá antes de recibir!"',
          '"Cabeza gira, cuerpo escanea"',
        ],
        commonMistakes: [
          'Mirar siempre al balón',
          'Gritar después del control (ya es tarde)',
          'No usar el tiempo del pase para mirar',
        ],
        variations: {
          easier: 'Pasar más lento, dar más tiempo para el scan.',
          harder: 'Aumentar velocidad del pase, agregar un 2do coach con otro número.',
        },
      },
      {
        time: '0:35-1:15',
        name: 'SSG temáticos con reglas provocadoras',
        content: 'Juegos 4v4 con 4 reglas provocadoras rotativas.',
        image: 'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=800&q=80',
        objective: 'Forzar toma de decisiones tácticas específicas mediante reglas que alteran el comportamiento natural (Constraints-Led Approach).',
        equipment: ['Balones', '4 arcos chicos', 'Petos 2 colores', 'Conos'],
        exercises: [
          {
            name: 'SSG #1 — Mínimo 3 pases antes del gol',
            duration: '10 min',
            description: 'Campo 25x20m. Para que un gol cuente, el equipo atacante debe hacer mínimo 3 pases consecutivos. Entrena construcción y paciencia.',
            keyPoints: [
              'Buscar el pase, no solo el tiro',
              'Crear superioridad numérica',
              'Circulación del balón',
            ],
          },
          {
            name: 'SSG #2 — Comodines neutrales',
            duration: '10 min',
            description: '4v4 + 2 comodines que juegan para el equipo que tiene el balón. Entrena reconocer superioridad (6v4 cuando tienen el balón).',
            keyPoints: [
              'Pasarle al comodín es seguro',
              'Crear opciones 2-3 pases adelante',
              'El comodín ayuda pero no finaliza',
            ],
          },
          {
            name: 'SSG #3 — Zonas prohibidas',
            duration: '10 min',
            description: 'Campo dividido en 3 zonas verticales. No se puede estacionar más de 3s en la zona central del rival. Entrena profundidad y movimiento.',
            keyPoints: [
              'Movimiento sin balón',
              'Uso de bandas',
              'Cambios de orientación',
            ],
          },
          {
            name: 'SSG #4 — Goles dobles por banda',
            duration: '10 min',
            description: 'Los goles metidos tras un centro desde banda (balón cruza el último 1/3 por lateral) valen doble. Entrena apertura y juego asociado.',
            keyPoints: [
              'Abrirse al recibir',
              'Buscar al extremo',
              'Centros tempranos',
            ],
          },
        ],
        coachCues: [
          '"¡Paciencia en el pase!"',
          '"¿Dónde está el espacio libre?"',
          '"¿Quién te marca?"',
        ],
        commonMistakes: [
          'Ignorar la regla y jugar "normal"',
          'No adaptarse cuando cambia la regla',
          'Amontonarse en una zona',
        ],
        variations: {
          easier: 'Usar una sola regla por sesión y repetirla.',
          harder: 'Combinar 2 reglas simultáneas.',
        },
      },
      {
        time: '1:15-1:45',
        name: 'Partido guiado con freeze',
        content: 'Partido 6v6 con pausas "freeze" cada 3-4 min. Coach hace preguntas socráticas.',
        image: 'https://images.unsplash.com/photo-1552667466-07770ae110d0?w=800&q=80',
        objective: 'Desarrollar consciencia táctica mediante preguntas (no respuestas) en momentos clave del juego.',
        equipment: ['Arcos grandes o medianos', 'Petos', 'Silbato'],
        exercises: [
          {
            name: 'Partido 6v6 con freeze didáctico',
            duration: '30 min',
            description: 'Partido normal. Cada 3-4 min el coach grita "¡FREEZE!" y todos quedan quietos en su posición. Coach selecciona a 1-2 jugadores y les hace preguntas: "¿Qué ves?", "¿Dónde está el espacio?", "¿Qué opción tenías?". Luego retoman.',
            keyPoints: [
              'No corregir, solo preguntar',
              'Dejar que el jugador descubra',
              'Freezes cortos (<60s cada uno)',
              'Rotación: preguntar a distintos jugadores',
            ],
          },
        ],
        coachCues: [
          '"¿Qué veías en ese momento?"',
          '"¿Qué otra opción tenías?"',
          '"Si pasara de nuevo, ¿qué harías diferente?"',
          '"¿Dónde estaba tu compañero libre?"',
        ],
        commonMistakes: [
          'Dar la respuesta en vez de preguntar',
          'Pausar demasiado (rompe el ritmo)',
          'Preguntar solo al que se equivocó (usar también buenos ejemplos)',
        ],
        variations: {
          easier: 'Preguntas más guiadas ("¿viste al 10 libre?"), freezes más cortos.',
          harder: 'Dejar que entre los jugadores debatan la respuesta. Freezes más tácticos (doble línea, etc.).',
        },
      },
      {
        time: '1:45-2:00',
        name: 'Cierre reflexivo',
        content: 'Video-feedback corto (2 min) si disponible + metacognición grupal + hidratación.',
        image: 'https://images.unsplash.com/photo-1519750157634-b6d493a0f77c?w=800&q=80',
        objective: 'Consolidar aprendizajes mediante reflexión metacognitiva (Zimmerman, 2002).',
        equipment: ['iPad/teléfono con grabación (opcional)', 'Agua'],
        exercises: [
          {
            name: 'Video-feedback (si hubo grabación)',
            duration: '4 min',
            description: 'Mostrar 2-3 clips cortos (30s cada uno) de decisiones del partido. Preguntar "¿Qué ves?" antes de dar opinión.',
            keyPoints: [
              'Mostrar ejemplos positivos + a mejorar',
              'Preguntas abiertas',
              'Dejar que identifiquen patrones',
            ],
          },
          {
            name: 'Círculo metacognitivo',
            duration: '5 min',
            description: '3 preguntas por jugador: (1) ¿Qué decisión buena tomaste hoy? (2) ¿Qué cambiarías? (3) ¿Qué aprendiste de un compañero?',
            keyPoints: [
              'Respetar turnos',
              'Especificidad en las respuestas',
              'Aprender del otro, no solo de sí mismo',
            ],
          },
          {
            name: 'Hidratación y cierre',
            duration: '6 min',
            description: 'Agua, frutas. Coach da feedback final grupal (2 cosas buenas, 1 objetivo para próxima).',
            keyPoints: [
              'Sandwich positivo',
              'Objetivo concreto y medible',
              'Energía alta al despedir',
            ],
          },
        ],
        coachCues: [
          '"¿Cuál fue tu mejor decisión?"',
          '"¿Qué aprendiste del equipo?"',
          '"¿Para la próxima, en qué te vas a enfocar?"',
        ],
        commonMistakes: [
          'Dar el feedback del coach antes de escuchar a los jugadores',
          'Foco solo en errores',
          'Saltar el cierre por tiempo',
        ],
        variations: {
          easier: 'Solo 1 pregunta metacognitiva en vez de 3.',
          harder: 'Que cada jugador le de feedback a otro (formal, constructivo).',
        },
      },
    ],
    loadLimits: [
      'Pausas de reflexión cada 10-15 min (capacidad atencional limitada)',
      'RPE mental 8/10, físico 5/10',
      'Evitar instrucciones prescriptivas — usar preguntas',
    ],
  },

  // ══════════════════════════════════════════════════════════════════
  // PLAN C — FÍSICO & NEUROMUSCULAR
  // ══════════════════════════════════════════════════════════════════
  {
    id: 'fisico',
    code: 'C',
    title: 'Físico & Neuromuscular',
    color: 'amber',
    objective: 'Velocidad, agilidad, fuerza funcional sin cargas externas',
    science: 'Youth Physical Development Model (Lloyd & Oliver, 2012). Ventana #1 de velocidad: 7-9 años. Velocidad pre-PHV responde mejor a estímulos neurales que metabólicos (Rumpf et al., 2012).',
    targetRPE: '6/10 (alta neural, baja metabólica)',
    blocks: [
      {
        time: '0:00-0:20',
        name: 'Activación + técnica de carrera',
        content: 'Movilidad dinámica + skipping + técnica de carrera (ABC atlético).',
        image: 'https://images.unsplash.com/photo-1434596922112-19c563067271?w=800&q=80',
        objective: 'Preparar sistema neuromuscular para trabajo de alta intensidad + mejorar eficiencia mecánica de carrera.',
        equipment: ['Conos para marcar 15m', 'Cinta métrica'],
        exercises: [
          {
            name: 'Movilidad dinámica global',
            duration: '5 min',
            description: 'Caminata activa + círculos de cadera / rodilla / tobillo / hombros. Zancadas con rotación de tronco. Estocadas con giro. Rango completo sin rebote.',
            keyPoints: [
              'Amplitud máxima',
              'Control en todo el recorrido',
              'Activar todas las articulaciones',
            ],
          },
          {
            name: 'ABC atlético progresivo',
            duration: '10 min',
            description: 'Serie de 15m cada ejercicio, vuelta al trote: (1) A-skip (rodilla alta), (2) B-skip (rodilla alta + extensión), (3) Talones al glúteo, (4) Zancadas altas, (5) Carioca, (6) Skipping lateral.',
            keyPoints: [
              'Contacto corto con el piso (pie "rebota")',
              'Brazos coordinados (90° codo)',
              'Cabeza arriba',
              'Cada ejercicio 2 veces',
            ],
          },
          {
            name: 'Aceleraciones progresivas',
            duration: '5 min',
            description: '4 corridas de 20m: 60%, 70%, 80%, 90% del máximo. Caminata de vuelta. Preparan al sistema nervioso para sprints reales.',
            keyPoints: [
              'Crecer progresivamente la intensidad',
              'Mecánica limpia > velocidad',
              'Recuperación completa entre corridas',
            ],
          },
        ],
        coachCues: [
          '"¡Rebotá con el pie, no aplastes!"',
          '"Brazos largos, codos a 90"',
          '"Mirá al frente, no al piso"',
        ],
        commonMistakes: [
          'Saltar el ABC y pasar directo a sprints',
          'Brazos cruzando el cuerpo',
          'Pisar con toda la planta (debe ser metatarso)',
        ],
        variations: {
          easier: 'Reducir distancia a 10m, menos ejercicios.',
          harder: 'Agregar saltos verticales entre ABCs.',
        },
      },
      {
        time: '0:20-0:45',
        name: 'Velocidad — sprints',
        content: 'Sprints 10-15m máx. 8 repeticiones. Recuperación completa ratio 1:10.',
        image: 'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=800&q=80',
        objective: 'Entrenar velocidad máxima (sistema ATP-PC) aprovechando la ventana sensible 7-9 años.',
        equipment: ['Cronómetro', 'Conos', 'Silbato', 'Pelota (para variante)'],
        exercises: [
          {
            name: 'Sprints lineales 15m',
            duration: '8 min (4 reps × 15m, descanso 60s entre cada uno)',
            description: 'Línea de salida + cono a 15m. Salida parado. Al silbato, máximo sprint hasta pasar el cono. Caminar de vuelta (descanso activo).',
            keyPoints: [
              'Salida explosiva (primeros 3 pasos son clave)',
              'Inclinación corporal hacia adelante',
              'Brazos rápidos',
              'Correr PASANDO el cono, no hasta',
            ],
          },
          {
            name: 'Sprints con salida reactiva',
            duration: '8 min (4 reps × 15m)',
            description: 'Mismo set up. Pero el jugador está de espaldas, de rodillas, o sentado. Al grito "¡ya!" se incorpora y sprinta. Entrena tiempo de reacción.',
            keyPoints: [
              'Reacción al estímulo auditivo',
              'Primer paso potente al levantarse',
              'No anticipar',
            ],
          },
          {
            name: 'Persecución 1v1',
            duration: '6 min (4 duelos)',
            description: 'Dos jugadores separados 2m. El de atrás persigue al de adelante cuando el coach silba. Ventaja de 1m para el perseguidor o 1s para el perseguido (según nivel).',
            keyPoints: [
              'Reacción + velocidad',
              'No mirar atrás',
              'El perseguidor ajusta su ángulo',
            ],
          },
        ],
        coachCues: [
          '"¡Exploten en los primeros 3 pasos!"',
          '"Brazos fuerte"',
          '"Pasá el cono, no frenes"',
        ],
        commonMistakes: [
          'Frenar antes de llegar al cono',
          'No respetar el descanso (sobrecarga y baja calidad)',
          'Sprints a menos del 95% (no es entrenamiento de velocidad)',
        ],
        variations: {
          easier: 'Distancia 10m, menos repeticiones.',
          harder: '20m, salidas más complejas (dos estímulos).',
        },
      },
      {
        time: '0:45-1:10',
        name: 'Agilidad & pliometría baja',
        content: 'Saltos a dos pies con caída controlada, cambios de dirección reactivos, escalera de coordinación. Máx 40 contactos de alto impacto.',
        image: 'https://images.unsplash.com/photo-1526676037777-05a232554f77?w=800&q=80',
        objective: 'Entrenar cambio de dirección y aterrizaje seguro (prevención de lesiones de rodilla/tobillo).',
        equipment: ['Escalera de coordinación', 'Conos', '6 aros o marcas en el piso'],
        exercises: [
          {
            name: 'Saltos verticales a 2 pies',
            duration: '5 min (3 series de 6 saltos, descanso 60s)',
            description: 'Salto vertical máximo con contramovimiento. Aterrizaje SUAVE, absorbiendo con rodillas alineadas. Pausa 3s antes del siguiente salto.',
            keyPoints: [
              'Rodilla sobre el dedo gordo (no caer hacia adentro)',
              'Aterrizaje con metatarso → talón',
              'Core firme',
            ],
          },
          {
            name: 'Saltos laterales sobre línea',
            duration: '5 min (4 series de 15s, descanso 45s)',
            description: 'Línea en el piso. Saltos laterales a 2 pies sobre la línea (ida y vuelta). Máxima frecuencia posible con buen control.',
            keyPoints: [
              'Contacto corto con el piso',
              'Aterrizaje suave',
              'No perder el equilibrio',
            ],
          },
          {
            name: 'Cambios de dirección reactivos',
            duration: '8 min',
            description: 'Jugador parte corriendo, coach señala con la mano dirección (izq/der). Jugador cambia. Distancia total 15-20m con 3-4 cambios.',
            keyPoints: [
              'Bajar el centro de gravedad en el cambio',
              'Pie de apoyo firme',
              'Re-acelerar tras el cambio',
            ],
          },
          {
            name: 'Escalera de coordinación',
            duration: '7 min',
            description: '5 patrones × 3 pasadas cada uno: (1) un pie por cuadro, (2) dos pies por cuadro, (3) laterales "in-in-out-out", (4) crossover, (5) un pie saltando.',
            keyPoints: [
              'Máxima frecuencia',
              'Contactos cortos',
              'Mirada al frente, no al piso',
            ],
          },
        ],
        coachCues: [
          '"Aterrizá como gato"',
          '"Rodilla en línea con el pie"',
          '"Pisá corto y rebotá"',
        ],
        commonMistakes: [
          'Rodilla hacia adentro al aterrizar (valgo)',
          'Aterrizaje ruidoso (falta de control)',
          'Sobrepasar los 40 contactos de alto impacto',
        ],
        variations: {
          easier: 'Saltar menos alto, quitar la escalera.',
          harder: 'Saltos con giro 90°, escalera con balón.',
        },
      },
      {
        time: '1:10-1:45',
        name: 'Juego aplicado — SSG con transiciones',
        content: 'SSG 3v3 en campo alargado (30x15m) para forzar transiciones rápidas.',
        image: 'https://images.unsplash.com/photo-1522778119026-d647f0596c20?w=800&q=80',
        objective: 'Aplicar la velocidad/agilidad entrenada en situación real con transiciones.',
        equipment: ['Balones', 'Arcos chicos', 'Petos', 'Conos'],
        exercises: [
          {
            name: 'SSG 3v3 campo alargado',
            duration: '35 min (5 series de 3 min + 2 min descanso activo)',
            description: 'Campo 30x15m (alargado para forzar transiciones largas). Partidos 3v3 con 2 arcos chicos. Cada vez que se mete gol o sale el balón, se sirve rápido desde el arquero.',
            keyPoints: [
              'Transición ataque→defensa inmediata',
              'Transición defensa→ataque explosiva',
              'Apoyar en carrera, no caminando',
              'Contra-pressing a los 5s de perder',
            ],
          },
        ],
        coachCues: [
          '"¡Transición ya!"',
          '"¡Corran todos juntos!"',
          '"Primer pase al espacio"',
        ],
        commonMistakes: [
          'Caminar después de perder el balón',
          'No aprovechar las transiciones (jugar "normal")',
          'Agotarse y bajar intensidad las series finales',
        ],
        variations: {
          easier: 'Campo más corto (25x15m), 4 series en vez de 5.',
          harder: 'Con comodín que forza superioridad. Campo 35x15m.',
        },
      },
      {
        time: '1:45-2:00',
        name: 'Cierre — core + movilidad + hidratación',
        content: 'Core isométrico suave + movilidad + hidratación obligatoria.',
        image: 'https://images.unsplash.com/photo-1599058917212-d750089bc07e?w=800&q=80',
        objective: 'Reforzar el core (prevención de lesiones), bajar la intensidad, y recuperar.',
        equipment: ['Colchonetas (opcional)', 'Agua', 'Frutas'],
        exercises: [
          {
            name: 'Core isométrico suave',
            duration: '5 min',
            description: '(1) Plank frontal 3×20s, (2) Side plank 2×15s por lado, (3) Bird dog 2×8 por lado. 30s descanso entre cada ejercicio.',
            keyPoints: [
              'Alineación corporal (línea recta cabeza-cadera)',
              'Respiración continua (no contener)',
              'Calidad > tiempo',
            ],
          },
          {
            name: 'Movilidad post-ejercicio',
            duration: '5 min',
            description: 'Movilidad articular suave dinámica (NO estirar estático). Rotaciones lentas, péndulo de piernas, abrir-cerrar cadera.',
            keyPoints: [
              'Sin rebote',
              'Rango cómodo',
              'Combinado con respiración profunda',
            ],
          },
          {
            name: 'Hidratación y feedback',
            duration: '5 min',
            description: 'Agua (150-200ml), fruta si hay. Feedback grupal: "¿Cómo se sintieron físicamente? ¿Qué fue más duro?".',
            keyPoints: [
              'Hidratación activa (no solo sorbos)',
              'Feedback corporal específico',
              'Registro de RPE',
            ],
          },
        ],
        coachCues: [
          '"Espalda recta en plank"',
          '"Respirá, no apretes"',
          '"¿Cómo te fue hoy? Del 1 al 10"',
        ],
        commonMistakes: [
          'Contener respiración en el plank',
          'Cadera colgando en el plank',
          'No hidratarse (en cancha suele pasar)',
        ],
        variations: {
          easier: 'Tiempos más cortos (10s plank), solo 2 ejercicios.',
          harder: 'Plank con variantes (con pierna elevada).',
        },
      },
    ],
    loadLimits: [
      '< 15 sprints máximos totales',
      '< 40 contactos pliométricos de alto impacto',
      'SIN entrenamiento aeróbico continuo prolongado',
      'SIN cargas externas (pesas, balones medicinales pesados)',
    ],
  },

  // ══════════════════════════════════════════════════════════════════
  // PLAN D — MENTAL & COMPETITIVO
  // ══════════════════════════════════════════════════════════════════
  {
    id: 'mental',
    code: 'D',
    title: 'Mental & Competitivo',
    color: 'purple',
    objective: 'Autorregulación, resiliencia, inteligencia emocional bajo presión',
    science: 'Desarrollo psicosocial en academias élite (Larsen et al., 2013). Olímpicos atribuyen rendimiento a habilidades mentales entrenadas desde formativas (Gould et al., 2002). Mindfulness en jóvenes mejora atención sostenida (Bühlmayer et al., 2017, Sports Med).',
    targetRPE: '5/10 físico, 9/10 mental',
    blocks: [
      {
        time: '0:00-0:15',
        name: 'Apertura reflexiva',
        content: 'Círculo: cada niño dice su objetivo personal del día + técnica de respiración 4-7-8. Visualización guiada de 2 min.',
        image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80',
        objective: 'Preparar mente y cuerpo mediante auto-regulación emocional (pre-performance routine).',
        equipment: ['Colchonetas o espacio en círculo'],
        exercises: [
          {
            name: 'Círculo de objetivos',
            duration: '5 min',
            description: 'Jugadores en círculo, sentados. Cada uno dice: "Hoy mi objetivo es ___" (una cosa concreta y personal). Coach modela primero.',
            keyPoints: [
              'Objetivos específicos y accionables',
              'No "ganar" sino "hacer tal cosa"',
              'Escuchar sin interrumpir',
            ],
          },
          {
            name: 'Respiración 4-7-8',
            duration: '4 min',
            description: 'Inspirar 4 segundos por la nariz, retener 7 segundos, exhalar 8 segundos por la boca. 3 ciclos completos. Activa el sistema parasimpático.',
            keyPoints: [
              'Respiración diafragmática (panza afuera al inhalar)',
              'Exhalar más largo que inhalar',
              'Ojos cerrados si les resulta cómodo',
            ],
          },
          {
            name: 'Visualización guiada',
            duration: '3 min',
            description: 'Coach narra: "Imaginate haciendo una jugada perfecta. Sentí los pies tocando el balón. Mirá el arco. Sentí la alegría después de hacerla bien". Los jugadores con ojos cerrados.',
            keyPoints: [
              'Detalles multisensoriales',
              'Emoción positiva',
              'Imagen desde primera persona',
            ],
          },
          {
            name: 'Activación progresiva',
            duration: '3 min',
            description: 'Trote ligero + movilidad para pasar de estado calmo al activo. Cierra la apertura reflexiva antes de ejercicios.',
            keyPoints: [
              'Transición clara',
              'No saltar directo al ejercicio intenso',
              'Mantener consciencia corporal',
            ],
          },
        ],
        coachCues: [
          '"¿Cuál es tu objetivo para hoy?"',
          '"Inspirá por la nariz... retené... exhalá largo"',
          '"Visualizá tu mejor versión"',
        ],
        commonMistakes: [
          'Saltarse la apertura por presión de tiempo',
          'Reírse durante la visualización (normalizar: es común al principio)',
          'Objetivos vagos ("jugar bien")',
        ],
        variations: {
          easier: 'Solo respiración 4-4 (4 in / 4 out), sin retención.',
          harder: 'Visualización más larga (5 min) con escenarios de presión.',
        },
      },
      {
        time: '0:15-0:40',
        name: 'Técnica bajo presión',
        content: 'Ejercicios técnicos con consecuencia lúdica al fallar. Exposición controlada al error sin castigo real.',
        image: 'https://images.unsplash.com/photo-1577223625816-7546f13df25d?w=800&q=80',
        objective: 'Entrenar ejecución técnica bajo activación emocional (simular presión de partido).',
        equipment: ['Balones', 'Conos', 'Arcos chicos', 'Cronómetro'],
        exercises: [
          {
            name: 'Duelos técnicos 1v1 con público',
            duration: '10 min',
            description: '2 jugadores compiten en una tarea (conducción rápida, tiros al ángulo) mientras los demás miran en círculo. El que falla hace penitencia lúdica (ej: cantar 3 segundos una canción).',
            keyPoints: [
              'Normalizar estar "en el centro"',
              'Penitencia es DIVERTIDA, nunca humillante',
              'Celebrar el intento, no solo el éxito',
            ],
          },
          {
            name: 'Tiros con consecuencia progresiva',
            duration: '8 min',
            description: '5 rondas de 3 tiros al arco desde 8m. Primera ronda: relajada. Cada ronda el coach sube la "tensión" artificial ("ahora pesa doble", "ahora todos miran"). Registrar aciertos por ronda.',
            keyPoints: [
              'Observar cómo cambia el rendimiento con presión',
              'Discutir al final: "¿qué cambió en tu cuerpo?"',
              'Auto-regulación entre tiros',
            ],
          },
          {
            name: 'Pase preciso con timer',
            duration: '7 min',
            description: 'En parejas a 10m, 10 pases en 30 segundos. Cuentan los que entran limpios en el círculo del compañero. Se compite por parejas.',
            keyPoints: [
              'Velocidad + precisión',
              'Concentración bajo tiempo',
              'Respiración entre intentos',
            ],
          },
        ],
        coachCues: [
          '"¿Cómo te sentís ahora?"',
          '"Respirá antes de pegarle"',
          '"Fallar está bien, es parte del aprendizaje"',
        ],
        commonMistakes: [
          'Burlarse de quien falla (STOP inmediato)',
          'Aceptar el abandono ("no puedo"): redirigir con "vamos de nuevo"',
          'Presión excesiva sin herramientas para manejarla',
        ],
        variations: {
          easier: 'Sin público, solo el coach observa.',
          harder: 'Agregar apuesta grupal (si el equipo falla >X, todos hacen 5 sentadillas).',
        },
      },
      {
        time: '0:40-1:15',
        name: 'Competencia por equipos',
        content: 'Torneo interno 3v3 con marcador visible. Rotación obligatoria de capitanía.',
        image: 'https://images.unsplash.com/photo-1540379708242-14a809bef941?w=800&q=80',
        objective: 'Experimentar competencia real con rotación de liderazgo y manejo de emociones en victoria/derrota.',
        equipment: ['Pizarra o papel para marcador', 'Cinta de capitán', 'Petos', 'Arcos chicos'],
        exercises: [
          {
            name: 'Torneo todos contra todos',
            duration: '30 min',
            description: '3 equipos de 3 jugadores. Partidos de 5 min cada uno. Cada equipo juega 2 partidos (rotación). El capitán CAMBIA cada partido. El capitán debe dar al menos 1 instrucción táctica durante el juego.',
            keyPoints: [
              'Todos pasan por ser capitán',
              'Marcador visible motiva pero también expone',
              'Coach observa liderazgo emergente',
            ],
          },
          {
            name: 'Ceremonia de marcador',
            duration: '5 min',
            description: 'Al final del torneo, el coach anuncia el ganador. Ambos equipos (ganador y perdedor) hacen un pasillo de felicitación. Se entrega un reconocimiento simbólico al mejor capitán observado.',
            keyPoints: [
              'Respeto al rival (pasillo)',
              'Celebración sin burla',
              'Reconocer liderazgo, no solo habilidad',
            ],
          },
        ],
        coachCues: [
          '"Capitán, ¿qué le decís a tu equipo?"',
          '"¿Cómo se motivan cuando van abajo?"',
          '"Respeten al rival, son sus compañeros"',
        ],
        commonMistakes: [
          'Siempre los mismos 2 jugadores son capitanes',
          'Burla del equipo perdedor',
          'Capitán micro-maneja sin escuchar',
        ],
        variations: {
          easier: 'Sin marcador, solo los partidos.',
          harder: 'Tarjetas de presión (el coach saca una tarjeta con una regla extra mitad de partido).',
        },
      },
      {
        time: '1:15-1:40',
        name: 'Escenario de alta presión',
        content: 'Tanda de penales / regla de gol de oro / partido con desventaja inicial para entrenar resiliencia.',
        image: 'https://images.unsplash.com/photo-1624526267942-ab0ff8a3e972?w=800&q=80',
        objective: 'Exponer controladamente a presión máxima para desarrollar resiliencia emocional.',
        equipment: ['Balones', 'Arcos', 'Petos'],
        exercises: [
          {
            name: 'Tanda de penales real',
            duration: '10 min',
            description: '2 equipos, todos tiran 1 penal. Orden elegido por el capitán. Drama máximo: coach cuenta al revés, el resto observa en círculo. Si hay empate, muerte súbita.',
            keyPoints: [
              'Respirar antes del penal',
              'Elegir el lado ANTES del tiro (no decidir mid-air)',
              'Felicitarse acierten o no',
            ],
          },
          {
            name: 'Partido con desventaja inicial',
            duration: '10 min',
            description: '2 equipos. Uno arranca 2-0 abajo en el marcador. Jugar 8 min. El que va abajo debe manejar la frustración y encontrar recursos para remontar.',
            keyPoints: [
              'No rendirse aunque vayan abajo',
              'Buscar al líder del equipo',
              'Comunicación activa',
            ],
          },
          {
            name: 'Reflexión post-presión',
            duration: '5 min',
            description: 'Círculo corto. Preguntas: "¿Qué sentiste antes del penal?", "¿Cómo manejaste ir abajo 2-0?", "¿Qué hiciste cuando erró un compañero?".',
            keyPoints: [
              'Validar todas las emociones',
              'Dar herramientas ("respiración", "foco en próxima jugada")',
              'Transformar la experiencia en aprendizaje',
            ],
          },
        ],
        coachCues: [
          '"¿Cómo late tu corazón ahora?"',
          '"Respirá profundo antes de tirar"',
          '"Un error no define el partido"',
        ],
        commonMistakes: [
          'Protegerlos de la presión (pierde el propósito)',
          'No dar herramientas para manejarla',
          'Burla al que erra el penal',
        ],
        variations: {
          easier: 'Solo tanda de penales, sin desventaja inicial.',
          harder: 'Agregar público real (padres observando).',
        },
      },
      {
        time: '1:40-2:00',
        name: 'Cierre reflexivo — journaling',
        content: 'Journaling guiado con 3 preguntas en tarjeta. Círculo final de agradecimiento.',
        image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=80',
        objective: 'Consolidar el aprendizaje emocional mediante escritura reflexiva + cerrar con gratitud.',
        equipment: ['Tarjetas impresas con las 3 preguntas', 'Lápices', 'Agua'],
        exercises: [
          {
            name: 'Journaling individual',
            duration: '10 min',
            description: 'Cada jugador recibe una tarjeta con 3 preguntas: (1) ¿Qué hice bien hoy?, (2) ¿Qué voy a mejorar?, (3) ¿A quién agradezco hoy?. Escribir en silencio durante 8 min.',
            keyPoints: [
              'Silencio absoluto (es parte del ritual)',
              'Escritura honesta, no "correcta"',
              'Guardar la tarjeta (armar historial)',
            ],
          },
          {
            name: 'Compartir circular (opcional)',
            duration: '5 min',
            description: 'Los que quieran comparten UNA de sus respuestas. Siempre voluntario. El coach empieza modelando.',
            keyPoints: [
              'Voluntariedad absoluta',
              'Escucha sin juicio',
              'Espacio seguro',
            ],
          },
          {
            name: 'Círculo de agradecimiento',
            duration: '5 min',
            description: 'Todos en círculo, tomados de los hombros. Cada uno dice "Gracias a [nombre] por ___" al compañero de la derecha. Cierra con aplauso grupal.',
            keyPoints: [
              'Específico (no "gracias por todo")',
              'Genuino',
              'Cerrar con energía positiva',
            ],
          },
        ],
        coachCues: [
          '"¿Qué descubriste hoy de ti mismo?"',
          '"Gracias, [nombre], por ___"',
          '"Buen trabajo equipo, nos vemos la próxima"',
        ],
        commonMistakes: [
          'Forzar a compartir a quien no quiere',
          'Saltar el agradecimiento (cierre débil)',
          'Tarjetas genéricas (personalizarlas con nombre y fecha)',
        ],
        variations: {
          easier: 'Solo verbal (sin escritura) si hay jugadores que aún no leen-escriben bien.',
          harder: '5 preguntas en vez de 3, con 1 enfocada en objetivo específico para próxima semana.',
        },
      },
    ],
    loadLimits: [
      'Evitar humillación pública ante errores',
      'Feedback siempre en sandwich: positivo → corrección → positivo',
      'Límite de frustración: si un niño se desborda, pausa individual',
    ],
  },
];

export const WEEKLY_MICROCYCLE = [
  { day: 'Lunes',     plan: 'A — Técnica',  intensity: 'Media' },
  { day: 'Martes',    plan: 'C — Físico',   intensity: 'Alta (neural)' },
  { day: 'Miércoles', plan: 'Descanso / juego libre no estructurado', intensity: '—' },
  { day: 'Jueves',    plan: 'B — Táctica',  intensity: 'Media-Alta (cognitiva)' },
  { day: 'Viernes',   plan: 'D — Mental',   intensity: 'Media' },
  { day: 'Sábado',    plan: 'Partido oficial', intensity: 'Alta' },
  { day: 'Domingo',   plan: 'Descanso activo (otro deporte recomendado)', intensity: 'Baja' },
];

export const GENERAL_GUIDELINES = [
  'Máximo 8-9h de entrenamiento organizado por semana (regla Jayanthi et al., 2015: horas semanales ≤ edad del niño).',
  'Diversificación deportiva reduce lesiones por sobreuso en 50%.',
  'Hidratación: 150ml cada 20 min durante la sesión (guidelines pediátricas ACSM).',
  'Ratio juego/ejercicio analítico: 70/30 (Côté et al., 2007 — deliberate play > deliberate practice en esta edad).',
  'Estiramiento estático NO recomendado en menores de 10 años (Behm et al., 2016) — usar movilidad dinámica.',
  'Capacidad atencional ~15-20 min continuos antes de rotar estímulo (Gathercole et al.).',
];
